/**
 * Wave 3.99z — Mountings (Semi-Finished Gold Pieces) master page.
 *
 * Handles the entire lifecycle for one company:
 *   • CRUD the master list (Mountings / Chains / Pech / Chaki …)
 *   • Record purchases (weight in grams) per master item
 *   • Inspect the per-item ledger with running balance
 *   • Overview closing stock across every mounting + per-purity
 *
 * Every action is gated by the new ``materials.mountings.*``
 * permission family — the sidebar link renders only when the caller
 * has ``materials.mountings.see``.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Coins, PackagePlus, ScrollText, ArrowUpFromLine, AlertTriangle, Component } from 'lucide-react';
import { extractError } from '@/lib/errors';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';

// Wave 3.99aa — Compact INR formatter (₹, no paise). Used to render
// live gold-marked mounting values across the summary strip, the
// per-item table row, and the Issue-to-Order dialog.
const formatINR = (n) => {
  const v = Number(n || 0);
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `₹${Math.round(v).toLocaleString('en-IN')}`;
  }
};

export default function MountingsMaster() {
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell
      title="Mountings"
      subtitle="Semi-Finished Gold Pieces (Mountings / Chains / Pech / Chaki). Weight-only stock in grams; excluded from karigar labour."
    >
      <CompanyGate>
        <MountingsContent companyId={effectiveCompanyId} />
      </CompanyGate>
    </AppShell>
  );
}

function MountingsContent({ companyId }) {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const canEditMaster = hasPermission('materials.mountings.edit_master');
  const canPurchase   = hasPermission('materials.mountings.record_purchase');
  // Wave 3.99z — Mountings can be issued to any open order form by users
  // with either the explicit issue permission or the Karigar preset
  // (`orders.issue_to_goldsmith`). No unattributed-goldsmith path is
  // supported — issuing must always be against a specific order.
  const canIssue      = hasPermission('materials.mountings.issue') || hasPermission('orders.issue_to_goldsmith');

  const [list, setList] = useState([]);
  const [purities, setPurities] = useState([]);
  const [summary, setSummary] = useState({ total_stock_grams: 0, by_purity: [] });
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);       // full doc when editing, null when creating
  const [purchaseFor, setPurchaseFor] = useState(null); // mounting doc when purchasing
  const [issueFor, setIssueFor] = useState(null);       // Wave 3.99z — mounting doc when issuing to an order
  const [delTarget, setDelTarget] = useState(null);

  const loadAll = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      // Critical mountings data — gated by ``materials.mountings.see``.
      // Gold purities are only needed for the Add/Edit purity dropdown and
      // are gated by a SEPARATE permission (``materials.gold.see``). A
      // mountings-only operator may lack it, so fetch purities independently
      // and swallow a 403 rather than blocking the whole mountings page.
      const [ml, sm] = await Promise.all([
        api.get('/mountings', { params: { company_id: companyId } }),
        api.get('/mountings/stock-summary', { params: { company_id: companyId } }),
      ]);
      setList(Array.isArray(ml.data?.items) ? ml.data.items : []);
      setSummary(sm.data || { total_stock_grams: 0, by_purity: [] });
      api.get('/gold/purities', { params: { company_id: companyId } })
        .then((pu) => setPurities(Array.isArray(pu.data) ? pu.data : []))
        .catch(() => setPurities([]));
    } catch (e) {
      toast.error(extractError(e, 'Failed to load mountings'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll();   }, [companyId]);

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return list;
    return list.filter((m) =>
      (m.name || '').toLowerCase().includes(q)
      || (m.purity_name || '').toLowerCase().includes(q)
      || (m.description || '').toLowerCase().includes(q)
    );
  }, [list, query]);

  return (
    <div className="space-y-4" data-testid="mountings-page">
      {/* Summary strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 grid-auto-wide">
        <Card className="p-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
            <Coins size={12} /> Total Stock
          </div>
          <div className="mt-1 text-2xl font-mono tabular-nums text-[var(--ink-navy)]" data-testid="mountings-total-stock">
            {Number(summary.total_stock_grams || 0).toFixed(3)} g
          </div>
          {/* Wave 3.99aa — live gold-marked total value */}
          <div className="mt-0.5 text-[13px] font-mono tabular-nums text-[var(--gold-accent,#B08D57)]" data-testid="mountings-total-value">
            {formatINR(summary.total_stock_value || 0)}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            across {list.length} mounting{list.length === 1 ? '' : 's'} · rate {summary.gold_rate_per_gram_999 ? `${formatINR(summary.gold_rate_per_gram_999)} /g` : 'not set'}
          </div>
        </Card>
        {(summary.by_purity || []).map((p) => (
          <Card key={p.purity_name} className="p-4">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">{p.purity_name}</div>
            <div className="mt-1 text-2xl font-mono tabular-nums text-[var(--ink-navy)]">
              {Number(p.stock_grams || 0).toFixed(3)} g
            </div>
            <div className="mt-0.5 text-[12px] font-mono tabular-nums text-[var(--gold-accent,#B08D57)]">
              {formatINR(p.stock_value || 0)}
            </div>
          </Card>
        ))}
      </div>

      {/* Toolbar */}
      <Card className="p-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, purity, description…"
            className="pl-8 h-9 text-sm"
            data-testid="mountings-search"
          />
        </div>
        {canEditMaster && (
          <Button
            onClick={() => { setEditing(null); setFormOpen(true); }}
            data-testid="mountings-add-btn"
            className="h-9"
          >
            <Plus size={14} className="mr-1" /> Add Mounting
          </Button>
        )}
      </Card>

      {/* Main table */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="mountings-table">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr className="text-left">
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Purity</th>
                <th className="px-3 py-2 font-medium">Description</th>
                <th className="px-3 py-2 font-medium text-right">Stock (g)</th>
                <th className="px-3 py-2 font-medium text-right">Value</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-slate-500">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-500" data-testid="mountings-empty">
                    {query
                      ? `No mountings match "${query}"`
                      : (canEditMaster
                          ? 'No mountings yet. Click "Add Mounting" to create your first master item.'
                          : 'No mountings yet.')
                    }
                  </td>
                </tr>
              )}
              {filtered.map((m) => (
                <tr
                  key={m.id}
                  className="hover:bg-slate-50/60"
                  data-testid={`mountings-row-${m.id}`}
                >
                  <td className="px-3 py-2 font-medium text-slate-800">{m.name}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10.5px]">
                      {m.purity_name || '—'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-slate-600 max-w-[280px] truncate" title={m.description || ''}>
                    {m.description || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-800">
                    {Number(m.stock_grams || 0).toFixed(3)}
                  </td>
                  {/* Wave 3.99aa — Live gold-marked value */}
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-[var(--gold-accent,#B08D57)]" data-testid={`mountings-value-${m.id}`}>
                    {formatINR(m.stock_value || 0)}
                  </td>
                  <td className="px-3 py-2">
                    {m.active === false
                      ? <Badge className="bg-slate-100 text-slate-600 text-[10px]">Inactive</Badge>
                      : <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Active</Badge>
                    }
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => navigate(`/mountings/${m.id}/ledger`)}
                        data-testid={`mountings-view-ledger-${m.id}`}
                        title="View ledger"
                      >
                        <ScrollText size={13} />
                      </Button>
                      {canPurchase && (
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => setPurchaseFor(m)}
                          data-testid={`mountings-add-purchase-${m.id}`}
                          title="Record purchase"
                        >
                          <PackagePlus size={13} />
                        </Button>
                      )}
                      {/* Wave 3.99z — Issue this mounting into an open order.
                          Generates a Goldsmith Issue Memo via /goldsmith-issues/. */}
                      {canIssue && (
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => setIssueFor(m)}
                          data-testid={`mountings-issue-to-order-${m.id}`}
                          title={Number(m.stock_grams || 0) <= 0
                            ? 'Out of stock — record a purchase first'
                            : 'Issue to an open order form'}
                          disabled={Number(m.stock_grams || 0) <= 0 || m.active === false}
                          className="text-[var(--accent-warm,#B45309)]"
                        >
                          <ArrowUpFromLine size={13} />
                        </Button>
                      )}
                      {canEditMaster && (
                        <>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => { setEditing(m); setFormOpen(true); }}
                            data-testid={`mountings-edit-${m.id}`}
                            title="Edit"
                          >
                            <Pencil size={13} />
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => setDelTarget(m)}
                            data-testid={`mountings-delete-${m.id}`}
                            title="Deactivate"
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 size={13} />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <MountingFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        purities={purities}
        companyId={companyId}
        onSaved={() => { setFormOpen(false); setEditing(null); loadAll(); }}
      />
      <PurchaseDialog
        mounting={purchaseFor}
        onOpenChange={(v) => !v && setPurchaseFor(null)}
        onSaved={() => { setPurchaseFor(null); loadAll(); }}
      />
      {/* Wave 3.99z — Issue mounting to an open order. Reuses
          /api/goldsmith-issues/ so a Goldsmith Issue Memo PDF is
          produced. */}
      <IssueToOrderDialog
        mounting={issueFor}
        companyId={companyId}
        onOpenChange={(v) => !v && setIssueFor(null)}
        onSaved={() => { setIssueFor(null); loadAll(); }}
      />

      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent data-testid="mountings-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate this mounting?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{delTarget?.name}&rdquo; will be hidden from selection but its ledger and past order issues remain intact. You can re-activate later from the edit dialog.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await api.delete(`/mountings/${delTarget.id}`);
                  toast.success('Mounting deactivated');
                  setDelTarget(null);
                  loadAll();
                } catch (e) { toast.error(extractError(e, 'Delete failed')); }
              }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="mountings-delete-confirm-btn"
            >Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
// Create / Edit dialog
// ============================================================================
function MountingFormDialog({ open, onOpenChange, editing, purities, companyId, onSaved }) {
  const [name, setName] = useState('');
  const [purityId, setPurityId] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(editing?.name || '');
      setPurityId(editing?.purity_id || '');
      setDescription(editing?.description || '');
      setActive(editing?.active !== false);
    }
  }, [open, editing]);

  const submit = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (!purityId) { toast.error('Gold purity is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/mountings/${editing.id}`, {
          name, purity_id: purityId, description, active,
        });
        toast.success('Mounting updated');
      } else {
        await api.post('/mountings', {
          name, purity_id: purityId, description,
        }, { params: { company_id: companyId } });
        toast.success('Mounting created');
      }
      onSaved?.();
    } catch (e) {
      toast.error(extractError(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="mounting-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Mounting' : 'Add Mounting'}</DialogTitle>
          <DialogDescription>
            Semi-finished gold pieces are tracked by weight (grams). Each master item is anchored to one gold purity.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Ring Mounting 6mm, Chain 20 inch, Pech, Chaki"
              data-testid="mounting-form-name"
            />
          </div>
          <div>
            <Label className="text-xs">Gold Purity *</Label>
            <Select value={purityId} onValueChange={setPurityId}>
              <SelectTrigger data-testid="mounting-form-purity"><SelectValue placeholder="Select purity" /></SelectTrigger>
              <SelectContent>
                {purities.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Description (optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Notes about size, source, batch, etc."
              rows={2}
              data-testid="mounting-form-description"
            />
          </div>
          {editing && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                data-testid="mounting-form-active"
              />
              <span>Active (available for purchase + issue)</span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="mounting-form-submit">
            {saving ? 'Saving…' : (editing ? 'Save' : 'Create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Record purchase dialog
// ============================================================================
function PurchaseDialog({ mounting, onOpenChange, onSaved }) {
  const [weight, setWeight] = useState('');
  const [ratePerGram, setRatePerGram] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const open = !!mounting;

  useEffect(() => {
    if (open) { setWeight(''); setRatePerGram(''); setReference(''); setNotes(''); }
  }, [open]);

  const purchaseValue = useMemo(() => {
    const g = parseFloat(weight);
    const r = parseFloat(ratePerGram);
    if (Number.isFinite(g) && Number.isFinite(r) && g > 0 && r > 0) return g * r;
    return null;
  }, [weight, ratePerGram]);

  const submit = async () => {
    const g = parseFloat(weight);
    if (!(g > 0)) { toast.error('Weight must be greater than zero'); return; }
    const r = ratePerGram === '' ? null : parseFloat(ratePerGram);
    if (r !== null && !(r >= 0)) { toast.error('Rate must be zero or greater'); return; }
    setSaving(true);
    try {
      await api.post(`/mountings/${mounting.id}/purchase`, {
        weight_grams: g,
        rate_per_gram: r,
        reference,
        notes,
      });
      toast.success('Stock added');
      onSaved?.();
    } catch (e) {
      toast.error(extractError(e, 'Purchase failed'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="mounting-purchase-dialog">
        <DialogHeader>
          <DialogTitle>Record Purchase — {mounting?.name}</DialogTitle>
          <DialogDescription>
            Adds stock in grams to this mounting&apos;s ledger. Current balance: <span className="font-mono">{Number(mounting?.stock_grams || 0).toFixed(3)} g</span> · Purity: {mounting?.purity_name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Weight (grams) *</Label>
              <Input
                type="number" step="0.001" min="0"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="e.g., 25.500"
                data-testid="mounting-purchase-weight"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs">Rate per gram (₹)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={ratePerGram}
                onChange={(e) => setRatePerGram(e.target.value)}
                placeholder="e.g., 6250"
                data-testid="mounting-purchase-rate"
              />
              <div className="mt-1 text-[10.5px] text-slate-500">
                {purchaseValue !== null
                  ? <>Purchase value: <span className="font-mono">₹{Math.round(purchaseValue).toLocaleString('en-IN')}</span></>
                  : 'Audit only — balance value marks to live gold rate.'}
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Reference / Bill # (optional)</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Vendor bill #, batch code…"
              data-testid="mounting-purchase-reference"
            />
          </div>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              data-testid="mounting-purchase-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="mounting-purchase-submit">
            {saving ? 'Saving…' : 'Record Purchase'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// Wave 3.99z — Issue-to-Order dialog
// ----------------------------------------------------------------------------
// Lets the operator issue a mounting directly into an open order form
// from the Mountings Master page. Reuses `/api/goldsmith-issues/` so a
// Goldsmith Issue Memo PDF is generated exactly like the OrderDetail
// flow. Only non-completed orders are pickable (per user spec).
// On purity mismatch, the operator is prompted for confirmation before
// the memo is committed.
// ============================================================================
function IssueToOrderDialog({ mounting, companyId, onOpenChange, onSaved }) {
  const navigate = useNavigate();
  const open = !!mounting;
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [weight, setWeight] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [mismatch, setMismatch] = useState(null); // { mounting_purity_name, order_purity_name }
  const [pendingAck, setPendingAck] = useState(false);

  // Reset local state whenever the dialog is (re)opened for a new mounting.
  useEffect(() => {
    if (!open) return;
    setOrderId('');
    setWeight('');
    setNote('');
    setMismatch(null);
    setPendingAck(false);
    let cancelled = false;
    (async () => {
      setOrdersLoading(true);
      try {
        // status=open → non-completed + non-cancelled orders (see backend
        // /api/orders GET). We fetch a slim projection (id + order_no +
        // goldsmith + purity) and rely on the list being pre-sorted by
        // most recent first.
        const { data } = await api.get('/orders/', {
          params: { company_id: companyId, status: 'open' },
        });
        if (!cancelled) {
          const opts = (Array.isArray(data) ? data : []).filter((o) => !!o?.goldsmith_id);
          setOrders(opts);
        }
      } catch (e) {
        if (!cancelled) toast.error(extractError(e, 'Failed to load orders'));
      } finally {
        if (!cancelled) setOrdersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, companyId]);

  const selectedOrder = useMemo(
    () => orders.find((o) => o.id === orderId) || null,
    [orders, orderId],
  );

  // Client-side purity mismatch check (mirrors OrderDetail dialog).
  const orderPurityId = selectedOrder?.jewellery_purity_id || selectedOrder?.gold_purity_id || null;
  const orderPurityName = selectedOrder?.jewellery_purity_name || selectedOrder?.gold_purity_name || 'unspecified';
  const wouldMismatch = !!(
    selectedOrder && mounting
    && orderPurityId && mounting.purity_id
    && mounting.purity_id !== orderPurityId
  );

  const doSubmit = async (ackFlag) => {
    if (!mounting || !selectedOrder) return;
    setSaving(true);
    try {
      const payload = {
        goldsmith_id: selectedOrder.goldsmith_id,
        company_id: companyId,
        reference: `From Mountings — ${mounting.name}`,
        note: '',
        lines: [{
          material_type: 'mounting',
          mounting_id: mounting.id,
          weight_grams: Number(weight),
          order_id: selectedOrder.id,
          order_no: selectedOrder.order_no,
          note: note || '',
          acknowledged_purity_mismatch: !!ackFlag,
        }],
      };
      const { data } = await api.post('/goldsmith-issues/', payload);
      const memoNo = data.issue_memo_no || data.issue_no || '';
      toast.success(`Issue Memo ${memoNo} created`, {
        action: {
          label: 'View Memo',
          onClick: () => navigate(`/issue-memos/${data.id}`),
        },
      });
      setMismatch(null);
      onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const isMismatch = e?.response?.status === 409
        && typeof detail === 'object'
        && detail?.code === 'purity_mismatch';
      if (isMismatch) {
        // Fallback for races: backend flagged a mismatch we didn't
        // catch client-side. Prompt for ack and retry.
        setMismatch({
          mounting_purity_name: detail.mounting_purity_name || mounting.purity_name || '—',
          order_purity_name: detail.order_purity_name || orderPurityName,
        });
      } else {
        toast.error(extractError(e, 'Issue failed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const submit = () => {
    if (!selectedOrder) { toast.error('Pick an order first'); return; }
    const g = parseFloat(weight);
    if (!(g > 0)) { toast.error('Weight must be greater than zero'); return; }
    if (g - Number(mounting?.stock_grams || 0) > 1e-6) {
      toast.error(`Not enough stock — only ${Number(mounting?.stock_grams || 0).toFixed(3)} g available.`);
      return;
    }
    // Client-side pre-flight purity mismatch check.
    if (wouldMismatch && !pendingAck) {
      setMismatch({
        mounting_purity_name: mounting?.purity_name || mounting?.purity_karat || '—',
        order_purity_name: orderPurityName,
      });
      return;
    }
    doSubmit(pendingAck || wouldMismatch);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" data-testid="mounting-issue-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpFromLine size={16} className="text-[var(--accent-warm,#B45309)]" />
              Issue to Order — {mounting?.name}
            </DialogTitle>
            <DialogDescription>
              Reduces this mounting&apos;s stock and generates a Goldsmith Issue Memo attributed to the picked order.
              Current stock: <span className="font-mono">{Number(mounting?.stock_grams || 0).toFixed(3)} g</span>
              {mounting?.purity_name ? <> · Purity: <span className="font-medium">{mounting.purity_name}</span></> : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Open Order *</Label>
              <SearchableSelect
                items={orders}
                value={orderId}
                onValueChange={(v) => setOrderId(v || '')}
                itemToValue={(o) => o.id}
                itemToLabel={(o) => {
                  const purity = o.jewellery_purity_name || o.gold_purity_name || '—';
                  const gs = o.goldsmith_name ? ` · ${o.goldsmith_name}` : '';
                  return `${o.order_no || '(no #)'} · ${purity}${gs}`;
                }}
                searchKeys={['order_no', 'goldsmith_name', 'jewellery_purity_name', 'gold_purity_name', 'customer_name']}
                placeholder={ordersLoading ? 'Loading orders…' : 'Pick an open order'}
                searchPlaceholder="Search order no / karigar / purity…"
                loading={ordersLoading}
                emptyText="No open orders with an assigned karigar."
                testId="mounting-issue-order-picker"
              />
              {selectedOrder && (
                <div className="text-[11px] text-slate-500 mt-1" data-testid="mounting-issue-order-meta">
                  Order purity: <span className="font-medium text-slate-700">{orderPurityName}</span>
                  {' · '}Karigar: <span className="font-medium text-slate-700">{selectedOrder.goldsmith_name || '—'}</span>
                </div>
              )}
              {wouldMismatch && (
                <div className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50/60 border border-amber-200 rounded px-2 py-1">
                  <AlertTriangle size={12} className="mt-0.5" />
                  <span>
                    Purity mismatch: mounting is <span className="font-medium">{mounting?.purity_name || '—'}</span>, order is <span className="font-medium">{orderPurityName}</span>. You&apos;ll be asked to confirm before issuing.
                  </span>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Weight (grams) *</Label>
              <Input
                type="number" step="0.001" min="0"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder={`e.g., ${Number(mounting?.stock_grams || 0).toFixed(3)}`}
                data-testid="mounting-issue-weight"
              />
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Reason, size, batch reference…"
                data-testid="mounting-issue-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={saving || !selectedOrder || !weight}
              data-testid="mounting-issue-submit"
              className="bg-[var(--ink-navy)] hover:bg-[var(--ink-navy)]/90 text-white"
            >
              {saving ? 'Issuing…' : 'Issue to Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purity mismatch confirmation — same wording as OrderDetail. */}
      <AlertDialog open={!!mismatch} onOpenChange={(v) => !v && setMismatch(null)}>
        <AlertDialogContent data-testid="mounting-issue-purity-mismatch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-amber-600" size={18} />
              Confirm purity mismatch
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  The mounting&apos;s gold purity does not match the order&apos;s gold purity.
                  Proceeding will still issue it and adjust net weight, but the mismatch is recorded permanently in the memo and ledger.
                </p>
                <ul className="mt-2 space-y-1 text-[12.5px] text-slate-700 border border-slate-200 rounded-md p-2 bg-slate-50/50">
                  <li className="flex items-start gap-2">
                    <Component size={12} className="mt-0.5 text-amber-700" />
                    <span>
                      <span className="font-medium">{mounting?.name || 'Mounting'}</span>
                      {' — is '}
                      <span className="font-medium text-amber-800">{mismatch?.mounting_purity_name || '—'}</span>
                      {', order purity is '}
                      <span className="font-medium text-amber-800">{mismatch?.order_purity_name || 'unspecified'}</span>
                      {'.'}
                    </span>
                  </li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={saving}
              data-testid="mounting-issue-purity-mismatch-cancel"
              onClick={() => setMismatch(null)}
            >
              Cancel — don&apos;t issue
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={saving}
              onClick={() => {
                setPendingAck(true);
                setMismatch(null);
                // Defer to next tick so pendingAck flushes before doSubmit reads it.
                setTimeout(() => doSubmit(true), 0);
              }}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="mounting-issue-purity-mismatch-proceed"
            >
              {saving ? 'Issuing…' : 'Proceed anyway'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
